import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";

import SntDialog from "@/components/SntDialog/SntDialog";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";

export interface ImpersonateReasonDialogProps {
  isShow: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

const isReasonValid = (reason: string): boolean => {
  const trimmed = reason.trim();
  return trimmed.length >= 10 && trimmed.split(/\s+/).length >= 2;
};

/**
 * Dialog shown before impersonating a named user (STIC-14399). The reason is
 * persisted to the audit log and must be meaningful (at least two words,
 * minimum 10 characters).
 */
const ImpersonateReasonDialog: React.FC<ImpersonateReasonDialogProps> = ({
  isShow,
  onCancel,
  onConfirm,
}) => {
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (isShow) {
      setReason("");
      setTouched(false);
    }
  }, [isShow]);

  const valid = isReasonValid(reason);

  return (
    <SntDialog
      isShow={isShow}
      onClose={onCancel}
      onHide={onCancel}
      title={translate(LanguageKey.impersonate_user_key)}
      saveTxt={translate(LanguageKey.impersonate_user_key)}
      closeTxt={translate(LanguageKey.cancel_key)}
      disabled={!valid}
      onSave={(onDialogClicked) => {
        setTouched(true);
        if (!valid) return;
        onDialogClicked();
        onConfirm(reason.trim());
      }}
    >
      {() => (
        <>
          <p className="mb-3">
            {translate(LanguageKey.please_provide_reason_to_impersonate_key)}
          </p>
          <Form.Group controlId="impersonate-reason">
            <Form.Label>
              {translate(LanguageKey.impersonation_reason_label_key)}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              autoFocus
              value={reason}
              placeholder={translate(
                LanguageKey.impersonation_reason_placeholder_key
              )}
              isInvalid={touched && !valid}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => setTouched(true)}
            />
            <Form.Control.Feedback type="invalid">
              {translate(LanguageKey.impersonation_reason_validation_key)}
            </Form.Control.Feedback>
          </Form.Group>
        </>
      )}
    </SntDialog>
  );
};

export default ImpersonateReasonDialog;
